// Datos enriquecidos de medios españoles
// Fuentes: Wikipedia, informes de transparencia, estudios académicos de sesgo mediático

export interface MedioData {
  id: string;
  name: string;
  
  // Clasificación política (más matizada)
  bias: 'izquierda' | 'centro-izquierda' | 'centro' | 'centro-derecha' | 'derecha';
  biasLabel: string;
  biasScore: number; // -100 (izq) a +100 (der)
  color: 'red' | 'gray' | 'blue';
  
  // Visual
  initials: string;
  logoColor: string;
  url: string;
  
  // Descripción corta (para tarjetas)
  tagline: string;
  
  // Información extendida
  ownership: {
    group: string;
    owners: string[];
    type: 'privado' | 'público' | 'cooperativo' | 'independiente';
    notes?: string;
  };
  
  founded: {
    year: number;
    context: string;
    founders?: string[];
  };
  
  editorial: {
    summary: string;
    values: string[];
    criticalOf: string[];
    alignedWith: string[];
    style: string;
  };
  
  audience: {
    profile: string;
    demographics: string[];
    reach: string;
  };
  
  funding: {
    model: string;
    transparency: 'alta' | 'media' | 'baja' | 'opaca';
    notes?: string;
  };
  
  controversies?: string[];
  
  // Datos técnicos
  format: 'digital' | 'papel+digital' | 'papel';
  frequency: 'diario' | 'continuo';
}

export const MEDIOS_DATA: Record<string, MedioData> = {
  'elpais': {
    id: 'elpais',
    name: 'El País',
    bias: 'centro-izquierda',
    biasLabel: 'Centro-izquierda',
    biasScore: -35,
    color: 'red',
    initials: 'EP',
    logoColor: 'bg-gradient-to-br from-red-600 to-red-700',
    url: 'https://elpais.com',
    tagline: 'Diario de referencia progresista, fundado en la Transición',
    
    ownership: {
      group: 'Grupo PRISA',
      owners: ['Amber Capital (29%)', 'Telefónica (9%)', 'Santander (4%)', 'HSBC', 'Famille Polanco'],
      type: 'privado',
      notes: 'PRISA cotiza en bolsa. Ha pasado por reestructuraciones financieras importantes.'
    },
    
    founded: {
      year: 1976,
      context: 'Fundado meses después de la muerte de Franco, se convirtió en símbolo de la Transición democrática y el periodismo de libertades.',
      founders: ['José Ortega Spottorno', 'Jesús de Polanco', 'Juan Luis Cebrián']
    },
    
    editorial: {
      summary: 'Línea progresista, europeísta y socialdemócrata. Históricamente cercano al PSOE, aunque mantiene cierta distancia crítica. Defiende el estado de derecho, los derechos civiles y las políticas sociales.',
      values: ['Europeísmo', 'Socialdemocracia', 'Laicismo', 'Derechos civiles', 'Unidad de España'],
      criticalOf: ['Nacionalismos periféricos', 'Populismos', 'Derecha radical'],
      alignedWith: ['PSOE históricamente', 'Progresismo moderado'],
      style: 'Periodismo de referencia, análisis profundo, tono institucional'
    },
    
    audience: {
      profile: 'Clase media-alta urbana, profesionales con formación universitaria, votantes progresistas moderados',
      demographics: ['25-65 años', 'Universitarios', 'Grandes ciudades', 'Centro-izquierda'],
      reach: 'Líder digital en español (90M+ visitas/mes)'
    },
    
    funding: {
      model: 'Publicidad + Suscripciones digitales',
      transparency: 'media',
      notes: 'Publica informe de accionistas pero no detalla ingresos por fuente'
    },
    
    controversies: [
      'Críticas por sesgo pro-PSOE en coberturas electorales',
      'Despidos masivos durante crisis de PRISA (2012-2013)',
      'Tensiones con Podemos por línea editorial crítica'
    ],
    
    format: 'papel+digital',
    frequency: 'diario'
  },
  
  'eldiario': {
    id: 'eldiario',
    name: 'elDiario.es',
    bias: 'izquierda',
    biasLabel: 'Izquierda',
    biasScore: -55,
    color: 'red',
    initials: 'ED',
    logoColor: 'bg-gradient-to-br from-green-600 to-green-700',
    url: 'https://eldiario.es',
    tagline: 'Periodismo a pesar de todo',
    
    ownership: {
      group: 'Diario de Prensa Digital S.L.',
      owners: ['Ignacio Escolar (director)', 'Socios trabajadores', 'Lectores socios'],
      type: 'independiente',
      notes: 'Modelo de socios (70.000+) que aportan estabilidad financiera sin depender de grandes grupos.'
    },
    
    founded: {
      year: 2012,
      context: 'Nace en plena crisis económica y de credibilidad de los medios tradicionales, apostando por el periodismo de investigación digital.',
      founders: ['Ignacio Escolar (ex-director de Público)', 'Olga Rodríguez', 'Juan Luis Sánchez']
    },
    
    editorial: {
      summary: 'Periodismo progresista comprometido con causas sociales. Fuerte énfasis en derechos humanos, transparencia y crítica al poder económico. Más a la izquierda que El País.',
      values: ['Justicia social', 'Derechos humanos', 'Feminismo', 'Ecologismo', 'Transparencia'],
      criticalOf: ['Corrupción', 'Poder económico', 'Derecha', 'Monarquía'],
      alignedWith: ['Izquierda progresista', 'Movimientos sociales'],
      style: 'Investigación, datos, enfoque social, activismo periodístico'
    },
    
    audience: {
      profile: 'Progresistas comprometidos, activistas, jóvenes universitarios, profesionales urbanos de izquierda',
      demographics: ['18-45 años', 'Universitarios', 'Urbano', 'Izquierda'],
      reach: '20M+ visitas/mes'
    },
    
    funding: {
      model: 'Socios (principal) + Publicidad limitada',
      transparency: 'alta',
      notes: 'Publica informe anual de transparencia con detalle de ingresos y gastos'
    },
    
    controversies: [
      'Acusaciones de sesgo pro-Podemos/Unidas Podemos',
      'Críticas por cobertura favorable a independentismo catalán'
    ],
    
    format: 'digital',
    frequency: 'continuo'
  },
  
  '20minutos': {
    id: '20minutos',
    name: '20 Minutos',
    bias: 'centro',
    biasLabel: 'Centro',
    biasScore: 0,
    color: 'gray',
    initials: '20',
    logoColor: 'bg-gradient-to-br from-orange-500 to-orange-600',
    url: 'https://20minutos.es',
    tagline: 'El medio de las mayorías silenciosas',
    
    ownership: {
      group: 'Grupo Henneo',
      owners: ['Familia Yarza (Henneo)', 'Grupo Heraldo'],
      type: 'privado',
      notes: 'Henneo es un grupo aragonés con base en Heraldo de Aragón.'
    },
    
    founded: {
      year: 2000,
      context: 'Nació como periódico gratuito de distribución en transporte público, revolucionando el modelo de prensa gratuita en España.',
      founders: ['Grupo Schibsted (Noruega)', 'Posteriormente vendido a Henneo']
    },
    
    editorial: {
      summary: 'Enfoque generalista sin línea ideológica marcada. Prioriza noticias virales y de amplio interés. Buscador de consensos.',
      values: ['Neutralidad', 'Accesibilidad', 'Inmediatez'],
      criticalOf: ['Sin posicionamiento claro'],
      alignedWith: ['Sin afiliación política clara'],
      style: 'Noticias breves, contenido viral, titulares llamativos'
    },
    
    audience: {
      profile: 'Audiencia masiva y diversa, usuarios de transporte público, consumidores de noticias rápidas',
      demographics: ['Todas las edades', 'Clase media', 'Urbano principalmente'],
      reach: '30M+ visitas/mes'
    },
    
    funding: {
      model: 'Publicidad (principal)',
      transparency: 'baja',
      notes: 'No publica informes de transparencia detallados'
    },
    
    format: 'papel+digital',
    frequency: 'continuo'
  },
  
  'lavanguardia': {
    id: 'lavanguardia',
    name: 'La Vanguardia',
    bias: 'centro',
    biasLabel: 'Centro / Catalanista moderado',
    biasScore: 5,
    color: 'gray',
    initials: 'LV',
    logoColor: 'bg-gradient-to-br from-stone-700 to-stone-800',
    url: 'https://lavanguardia.com',
    tagline: 'El decano de la prensa catalana',
    
    ownership: {
      group: 'Grupo Godó',
      owners: ['Familia Godó (Conde de Godó)'],
      type: 'privado',
      notes: 'Grupo familiar centenario, también propietario de RAC1 y otros medios catalanes.'
    },
    
    founded: {
      year: 1881,
      context: 'Fundado durante la Restauración borbónica, representa la voz de la burguesía industrial catalana. Es el diario más antiguo de Barcelona en circulación.',
      founders: ['Familia Godó (Carlos y Bartolomé Godó)']
    },
    
    editorial: {
      summary: 'Catalanista moderado, liberal económicamente, defensor de los intereses de la burguesía catalana. Navega entre el catalanismo y el constitucionalismo según el momento político.',
      values: ['Catalanismo moderado', 'Liberalismo económico', 'Estabilidad institucional'],
      criticalOf: ['Extremismos', 'Populismos', 'Radicalismo independentista'],
      alignedWith: ['Burguesía catalana', 'Empresariado', 'Centroderecha catalán'],
      style: 'Periodismo tradicional, análisis económico, enfoque regional'
    },
    
    audience: {
      profile: 'Burguesía y clase media-alta catalana, empresarios, profesionales de Barcelona y área metropolitana',
      demographics: ['35-70 años', 'Catalunya principalmente', 'Clase media-alta'],
      reach: '40M+ visitas/mes'
    },
    
    funding: {
      model: 'Publicidad + Suscripciones',
      transparency: 'media'
    },
    
    controversies: [
      'Oscilaciones en posición sobre independentismo catalán',
      'Dependencia de publicidad institucional de la Generalitat'
    ],
    
    format: 'papel+digital',
    frequency: 'diario'
  },
  
  'elconfidencial': {
    id: 'elconfidencial',
    name: 'El Confidencial',
    bias: 'centro-derecha',
    biasLabel: 'Centro-derecha liberal',
    biasScore: 25,
    color: 'blue',
    initials: 'EC',
    logoColor: 'bg-gradient-to-br from-blue-700 to-blue-800',
    url: 'https://elconfidencial.com',
    tagline: 'El diario de los lectores influyentes',
    
    ownership: {
      group: 'Titania Compañía Editorial',
      owners: ['José Antonio Sánchez de la Vega', 'Accionistas varios', 'Trabajadores'],
      type: 'privado',
      notes: 'Pionero digital español, mantiene independencia de grandes grupos mediáticos.'
    },
    
    founded: {
      year: 2001,
      context: 'Nació como web de información económica confidencial, evolucionando a medio generalista de referencia digital.',
      founders: ['Jesús Cacho', 'José Antonio Sánchez de la Vega']
    },
    
    editorial: {
      summary: 'Liberal económicamente, enfocado en información empresarial y financiera. Periodismo de datos e investigación. Posición crítica con todos los gobiernos pero más duro con la izquierda.',
      values: ['Liberalismo económico', 'Periodismo de datos', 'Transparencia financiera'],
      criticalOf: ['Intervencionismo estatal', 'Populismo', 'Corrupción'],
      alignedWith: ['Élites económicas', 'Liberales'],
      style: 'Periodismo de investigación, datos, enfoque económico'
    },
    
    audience: {
      profile: 'Directivos, inversores, economistas, profesionales del sector financiero y empresarial',
      demographics: ['30-60 años', 'Alta formación', 'Alto poder adquisitivo'],
      reach: '50M+ visitas/mes'
    },
    
    funding: {
      model: 'Publicidad premium + Suscripciones',
      transparency: 'media'
    },
    
    controversies: [
      'Filtraciones controvertidas (Caso Villarejo)',
      'Acusaciones de agenda anti-gobierno de izquierdas'
    ],
    
    format: 'digital',
    frequency: 'continuo'
  },
  
  'elespanol': {
    id: 'elespanol',
    name: 'El Español',
    bias: 'centro-derecha',
    biasLabel: 'Centro-derecha',
    biasScore: 35,
    color: 'blue',
    initials: 'EE',
    logoColor: 'bg-gradient-to-br from-yellow-500 to-yellow-600',
    url: 'https://elespanol.com',
    tagline: 'El periódico digital de Pedro J. Ramírez',
    
    ownership: {
      group: 'El Español Publicaciones S.A.',
      owners: ['Pedro J. Ramírez (mayoritario)', 'Inversores de crowdfunding inicial'],
      type: 'privado',
      notes: 'Fundado con campaña de crowdfunding récord (3,6M€). Pedro J. mantiene control editorial.'
    },
    
    founded: {
      year: 2015,
      context: 'Pedro J. Ramírez lo funda tras ser destituido de El Mundo, recaudando fondos mediante crowdfunding ciudadano.',
      founders: ['Pedro J. Ramírez']
    },
    
    editorial: {
      summary: 'Liberal-conservador, monárquico pero crítico con la Casa Real cuando hay escándalos. Anti-nacionalismos periféricos, defensor de la unidad de España.',
      values: ['Constitucionalismo', 'Monarquía parlamentaria', 'Unidad de España', 'Liberalismo'],
      criticalOf: ['Nacionalismos periféricos', 'Populismo de izquierda', 'Sanchismo'],
      alignedWith: ['Derecha moderada', 'PP', 'Constitucionalismo'],
      style: 'Opinión fuerte, investigación, multimedia (vídeo)'
    },
    
    audience: {
      profile: 'Antiguos lectores de El Mundo bajo Pedro J., derecha moderada, constitucionalistas',
      demographics: ['35-65 años', 'Centro-derecha', 'Urbano'],
      reach: '35M+ visitas/mes'
    },
    
    funding: {
      model: 'Publicidad + Suscripciones',
      transparency: 'media'
    },
    
    controversies: [
      'Campaña constante contra Pedro Sánchez',
      'Acusaciones de sesgo anti-independentismo extremo'
    ],
    
    format: 'digital',
    frequency: 'continuo'
  },
  
  'elmundo': {
    id: 'elmundo',
    name: 'El Mundo',
    bias: 'derecha',
    biasLabel: 'Derecha liberal',
    biasScore: 45,
    color: 'blue',
    initials: 'EM',
    logoColor: 'bg-gradient-to-br from-blue-500 to-blue-600',
    url: 'https://elmundo.es',
    tagline: 'Líder en información',
    
    ownership: {
      group: 'Unidad Editorial',
      owners: ['RCS MediaGroup (Italia)', 'Familia Berlusconi tenía participación'],
      type: 'privado',
      notes: 'Unidad Editorial también edita Marca y Expansión.'
    },
    
    founded: {
      year: 1989,
      context: 'Fundado por Pedro J. Ramírez, se posicionó como alternativa a El País con periodismo de investigación agresivo. Los casos GAL y Roldán le dieron prestigio.',
      founders: ['Pedro J. Ramírez', 'Alfonso de Salas', 'Balbino Fraga', 'Juan González']
    },
    
    editorial: {
      summary: 'Liberal-conservador, históricamente muy crítico con gobiernos del PSOE. Tradición de periodismo de investigación que destapó escándalos (GAL, Filesa). Actualmente más alineado con el PP.',
      values: ['Liberalismo', 'Periodismo de investigación', 'Anti-corrupción'],
      criticalOf: ['PSOE', 'Nacionalismos', 'Intervencionismo estatal'],
      alignedWith: ['PP', 'Derecha liberal'],
      style: 'Investigación, opinión potente, confrontación política'
    },
    
    audience: {
      profile: 'Derecha moderada y liberal, profesionales, clase media-alta conservadora',
      demographics: ['30-65 años', 'Centro-derecha', 'Nacional'],
      reach: '60M+ visitas/mes'
    },
    
    funding: {
      model: 'Publicidad + Suscripciones',
      transparency: 'media'
    },
    
    controversies: [
      'Destitución de Pedro J. Ramírez (2014) por presiones políticas',
      'Acusaciones de sesgo pro-PP',
      'Pérdida de credibilidad tras salida de Pedro J.'
    ],
    
    format: 'papel+digital',
    frequency: 'diario'
  },
  
  'abc': {
    id: 'abc',
    name: 'ABC',
    bias: 'derecha',
    biasLabel: 'Derecha conservadora',
    biasScore: 60,
    color: 'blue',
    initials: 'ABC',
    logoColor: 'bg-gradient-to-br from-slate-800 to-slate-900',
    url: 'https://abc.es',
    tagline: 'Diario centenario, monárquico y conservador',
    
    ownership: {
      group: 'Grupo Vocento',
      owners: ['Familias vascas tradicionales', 'Cotiza en bolsa'],
      type: 'privado',
      notes: 'Vocento también controla El Correo, El Diario Vasco y otros regionales.'
    },
    
    founded: {
      year: 1903,
      context: 'Fundado durante el reinado de Alfonso XIII, ABC ha mantenido su línea monárquica y conservadora durante más de un siglo, incluyendo el franquismo.',
      founders: ['Torcuato Luca de Tena']
    },
    
    editorial: {
      summary: 'Conservador tradicional, católico, monárquico ferviente. El más a la derecha de los diarios tradicionales. Defensor de valores tradicionales y la unidad de España.',
      values: ['Monarquía', 'Catolicismo', 'Tradición', 'Unidad de España', 'Familia tradicional'],
      criticalOf: ['Izquierda', 'Laicismo', 'Nacionalismos periféricos', 'Progresismo social'],
      alignedWith: ['PP', 'Derecha tradicional', 'Iglesia Católica'],
      style: 'Conservador clásico, editorial fuerte, defensa de instituciones'
    },
    
    audience: {
      profile: 'Derecha tradicional, católicos practicantes, personas mayores, monárquicos convencidos',
      demographics: ['50+ años principalmente', 'Conservadores', 'Católicos'],
      reach: '40M+ visitas/mes'
    },
    
    funding: {
      model: 'Publicidad + Suscripciones',
      transparency: 'media'
    },
    
    controversies: [
      'Pasado franquista (apoyó el régimen)',
      'Posiciones contra derechos LGTBI',
      'Cobertura muy sesgada de temas sociales'
    ],
    
    format: 'papel+digital',
    frequency: 'diario'
  },
  
  'okdiario': {
    id: 'okdiario',
    name: 'OK Diario',
    bias: 'derecha',
    biasLabel: 'Derecha / Sensacionalista',
    biasScore: 75,
    color: 'blue',
    initials: 'OK',
    logoColor: 'bg-gradient-to-br from-red-500 to-red-600',
    url: 'https://okdiario.com',
    tagline: 'El sitio de los valientes',
    
    ownership: {
      group: 'OK Diario S.L.',
      owners: ['Eduardo Inda (director y propietario)'],
      type: 'privado',
      notes: 'Financiación opaca. Se han reportado conexiones con fondos de derecha.'
    },
    
    founded: {
      year: 2015,
      context: 'Eduardo Inda (ex-director de Marca) lo funda posicionándose como medio combativo contra la izquierda y el independentismo.',
      founders: ['Eduardo Inda']
    },
    
    editorial: {
      summary: 'Muy agresivo contra la izquierda y los nacionalismos. Estilo sensacionalista, priorizando exclusivas polémicas y contenido viral. Cuestionada veracidad de algunas informaciones.',
      values: ['Anti-izquierda', 'Unidad de España', 'Combatividad'],
      criticalOf: ['PSOE', 'Podemos/Sumar', 'Independentismo', 'Pedro Sánchez'],
      alignedWith: ['Derecha', 'VOX en algunos temas'],
      style: 'Sensacionalista, exclusivas polémicas, clickbait, confrontación'
    },
    
    audience: {
      profile: 'Derecha movilizada, consumidores de contenido anti-izquierda, usuarios de redes sociales de derecha',
      demographics: ['25-60 años', 'Derecha', 'Alto consumo en redes'],
      reach: '50M+ visitas/mes'
    },
    
    funding: {
      model: 'Publicidad programática + Fuentes no transparentes',
      transparency: 'opaca',
      notes: 'No publica información sobre financiación. Investigaciones sugieren posibles fondos irregulares.'
    },
    
    controversies: [
      'Múltiples noticias desmentidas o sin verificar',
      'Relación con Comisario Villarejo (filtraciones)',
      'Financiación opaca investigada',
      'Condenas por vulnerar honor'
    ],
    
    format: 'digital',
    frequency: 'continuo'
  },
  
  'libertaddigital': {
    id: 'libertaddigital',
    name: 'Libertad Digital',
    bias: 'derecha',
    biasLabel: 'Derecha liberal-conservadora',
    biasScore: 70,
    color: 'blue',
    initials: 'LD',
    logoColor: 'bg-gradient-to-br from-sky-600 to-sky-700',
    url: 'https://libertaddigital.com',
    tagline: 'Sin amos, sin ataduras',
    
    ownership: {
      group: 'Libertad Digital S.A.',
      owners: ['Federico Jiménez Losantos', 'Accionistas afines'],
      type: 'privado',
      notes: 'Estrechamente ligado a esRadio. Losantos es figura central.'
    },
    
    founded: {
      year: 2000,
      context: 'Uno de los primeros digitales españoles, fundado como plataforma liberal-conservadora alternativa a medios mainstream.',
      founders: ['Federico Jiménez Losantos', 'Alberto Recarte']
    },
    
    editorial: {
      summary: 'Liberal en lo económico, conservador en lo social. Muy beligerante contra la izquierda y los nacionalismos periféricos. Losantos marca la línea desde esRadio.',
      values: ['Liberalismo económico', 'Anti-nacionalismos', 'Conservadurismo social', 'Españolismo'],
      criticalOf: ['PSOE', 'Nacionalismos', 'Progresismo', 'Consensos de la Transición a veces'],
      alignedWith: ['Derecha dura', 'VOX en algunos temas', 'PP ala dura'],
      style: 'Opinión muy marcada, beligerancia ideológica, multimedia (radio)'
    },
    
    audience: {
      profile: 'Derecha ideológica, oyentes de esRadio, anti-izquierda convencida',
      demographics: ['40-70 años', 'Derecha', 'Oyentes de radio opinión'],
      reach: '15M+ visitas/mes'
    },
    
    funding: {
      model: 'Publicidad + Suscripciones esRadio',
      transparency: 'baja'
    },
    
    controversies: [
      'Declaraciones polémicas de Losantos',
      'Posiciones extremas sobre inmigración',
      'Teorías de conspiración ocasionales'
    ],
    
    format: 'digital',
    frequency: 'continuo'
  },

  'infolibre': {
    id: 'infolibre',
    name: 'infoLibre',
    bias: 'izquierda',
    biasLabel: 'Izquierda',
    biasScore: -55,
    color: 'red',
    initials: 'IL',
    logoColor: 'bg-gradient-to-br from-purple-600 to-purple-700',
    url: 'https://www.infolibre.es',
    tagline: 'Periodismo independiente financiado por sus socios',
    
    ownership: {
      group: 'Ediciones Prensa Libre',
      owners: ['Socios lectores (mayoritario)', 'Jesús Maraña', 'Manuel Rico'],
      type: 'cooperativo',
      notes: 'Modelo de socios similar a elDiario.es. Los lectores son copropietarios.'
    },
    
    founded: {
      year: 2013,
      context: 'Fundado por periodistas veteranos tras la crisis de medios tradicionales, apostando por un modelo de suscripción y transparencia.',
      founders: ['Jesús Maraña', 'Manuel Rico', 'Alejandro Torrús']
    },
    
    editorial: {
      summary: 'Periodismo de investigación con línea progresista clara. Muy crítico con la corrupción y los poderes económicos. Defiende causas sociales y los servicios públicos.',
      values: ['Periodismo de investigación', 'Transparencia', 'Justicia social', 'Servicios públicos'],
      criticalOf: ['Corrupción (todos los partidos)', 'Poderes económicos', 'Derecha', 'Monarquía'],
      alignedWith: ['Izquierda', 'Movimientos sociales'],
      style: 'Investigación profunda, denuncia, análisis crítico'
    },
    
    audience: {
      profile: 'Izquierda comprometida, interesados en periodismo de investigación',
      demographics: ['30-60 años', 'Universitarios', 'Izquierda', 'Activistas'],
      reach: '5M+ visitas/mes'
    },
    
    funding: {
      model: 'Suscripciones de socios (principal) + publicidad limitada',
      transparency: 'alta',
      notes: 'Publican cuentas anuales. Modelo similar a elDiario.es'
    },
    
    controversies: [
      'Acusaciones de sesgo pro-Podemos en sus inicios'
    ],
    
    format: 'digital',
    frequency: 'continuo'
  },

  'publico': {
    id: 'publico',
    name: 'Público',
    bias: 'izquierda',
    biasLabel: 'Izquierda',
    biasScore: -60,
    color: 'red',
    initials: 'PU',
    logoColor: 'bg-gradient-to-br from-red-500 to-orange-600',
    url: 'https://www.publico.es',
    tagline: 'Diario digital de izquierdas, renacido tras su cierre en papel',
    
    ownership: {
      group: 'Display Connectors (Roures)',
      owners: ['Jaume Roures', 'Grupo Mediapro'],
      type: 'privado',
      notes: 'Refundado en 2012 como digital tras el cierre del diario en papel.'
    },
    
    founded: {
      year: 2007,
      context: 'Fundado como diario en papel, cerró en 2012 y renació como digital. Vinculado a Mediapro/Roures.',
      founders: ['Jaume Roures', 'Ignacio Escolar (primer director)']
    },
    
    editorial: {
      summary: 'Línea claramente de izquierdas, muy crítico con la derecha y los poderes económicos. Apoya causas progresistas, feminismo, memoria histórica.',
      values: ['Progresismo', 'Feminismo', 'Memoria histórica', 'República', 'Derechos sociales'],
      criticalOf: ['PP', 'VOX', 'Monarquía', 'Poderes económicos', 'Iglesia'],
      alignedWith: ['PSOE', 'Podemos/Sumar', 'Izquierda'],
      style: 'Opinión marcada, activismo periodístico, tribunas'
    },
    
    audience: {
      profile: 'Izquierda, votantes progresistas, activistas sociales',
      demographics: ['25-55 años', 'Izquierda', 'Urbano'],
      reach: '20M+ visitas/mes'
    },
    
    funding: {
      model: 'Publicidad + Suscripciones',
      transparency: 'media'
    },
    
    controversies: [
      'Cerró en papel dejando deudas con trabajadores',
      'Acusaciones de ser altavoz de la izquierda'
    ],
    
    format: 'digital',
    frequency: 'continuo'
  },

  'elplural': {
    id: 'elplural',
    name: 'El Plural',
    bias: 'izquierda',
    biasLabel: 'Izquierda',
    biasScore: -50,
    color: 'red',
    initials: 'PL',
    logoColor: 'bg-gradient-to-br from-blue-600 to-blue-700',
    url: 'https://www.elplural.com',
    tagline: 'Digital progresista con fuerte presencia en redes',
    
    ownership: {
      group: 'Grupo El Plural',
      owners: ['Angélica Rubio'],
      type: 'privado',
      notes: 'Medio digital independiente, propiedad de su directora.'
    },
    
    founded: {
      year: 2004,
      context: 'Uno de los primeros digitales nativos españoles, posicionado desde el inicio en el espacio progresista.',
      founders: ['Angélica Rubio']
    },
    
    editorial: {
      summary: 'Digital progresista, cercano al PSOE. Muy activo en redes sociales. Mezcla información con opinión de forma explícita.',
      values: ['Progresismo', 'Socialdemocracia', 'Derechos civiles'],
      criticalOf: ['PP', 'VOX', 'Derecha mediática'],
      alignedWith: ['PSOE', 'Progresismo'],
      style: 'Ágil, redes sociales, opinión explícita'
    },
    
    audience: {
      profile: 'Votantes del PSOE y progresistas, usuarios de redes sociales',
      demographics: ['30-60 años', 'Centro-izquierda', 'Muy activos en Twitter'],
      reach: '8M+ visitas/mes'
    },
    
    funding: {
      model: 'Publicidad',
      transparency: 'media'
    },
    
    format: 'digital',
    frequency: 'continuo'
  },

  'elperiodico': {
    id: 'elperiodico',
    name: 'El Periódico',
    bias: 'centro-izquierda',
    biasLabel: 'Centro-izquierda',
    biasScore: -30,
    color: 'red',
    initials: 'PE',
    logoColor: 'bg-gradient-to-br from-red-600 to-red-800',
    url: 'https://www.elperiodico.com',
    tagline: 'Diario catalán progresista con edición en castellano',
    
    ownership: {
      group: 'Prensa Ibérica',
      owners: ['Familia Moll', 'Prensa Ibérica'],
      type: 'privado',
      notes: 'Desde 2020 pertenece a Prensa Ibérica (editora de Información, Levante, etc.)'
    },
    
    founded: {
      year: 1978,
      context: 'Fundado en Barcelona durante la Transición, se posicionó como alternativa progresista a La Vanguardia.',
      founders: ['Antonio Asensio']
    },
    
    editorial: {
      summary: 'Línea progresista y catalanista moderada. Más popular y directo que La Vanguardia. Apoya el diálogo sobre Catalunya pero desde posiciones no independentistas.',
      values: ['Progresismo', 'Catalanismo moderado', 'Periodismo popular', 'Europeísmo'],
      criticalOf: ['Derecha española', 'Independentismo radical', 'Centralismo'],
      alignedWith: ['PSC', 'Catalanismo de izquierdas no independentista'],
      style: 'Popular, visual, cercano, bilingüe'
    },
    
    audience: {
      profile: 'Catalanes progresistas, clase media, no independentistas pero catalanistas',
      demographics: ['30-60 años', 'Catalunya', 'Centro-izquierda', 'Bilingües'],
      reach: '25M+ visitas/mes'
    },
    
    funding: {
      model: 'Publicidad + Suscripciones + Grupo Prensa Ibérica',
      transparency: 'media'
    },
    
    controversies: [
      'Vendido a Prensa Ibérica tras dificultades financieras',
      'EREs y reducción de plantilla'
    ],
    
    format: 'papel+digital',
    frequency: 'diario'
  }
};

// Helpers
export const getMedioById = (id: string): MedioData | undefined => MEDIOS_DATA[id];

export const getAllMedios = (): MedioData[] => Object.values(MEDIOS_DATA);

export const getMediosByBias = (bias: MedioData['bias']): MedioData[] => 
  getAllMedios().filter(m => m.bias === bias);

export const getMediosSorted = (): MedioData[] => 
  getAllMedios().sort((a, b) => a.biasScore - b.biasScore);

// Agrupar por espectro para la UI
export const getMediosGrouped = () => {
  const all = getMediosSorted();
  return {
    izquierda: all.filter(m => m.biasScore < -20),
    centro: all.filter(m => m.biasScore >= -20 && m.biasScore <= 20),
    derecha: all.filter(m => m.biasScore > 20)
  };
};
